import { useEffect, useEffectEvent, useMemo } from 'react';
import { FieldError, FieldErrors, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanDropdown } from '@/components';
import { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import {
    AutomationFormData,
    automationFormSchema,
    getDefaultAutomationFormValues,
} from '../validations/automationForm';
import { useAutomationFormActions } from '../hooks/useAutomationFormActions';
import { AutoDMField, CommentReplyField, KeywordInput, PostPicker, TriggerSelector } from './form-fields';
import type { Automation } from '../types/automation.types';
import IcMessage from '@/assets/icons/ic-message.svg';
import IcSend01 from '@/assets/icons/ic-send-01.svg';
import IcReverseLeft from '@/assets/icons/ic-reverse-left.svg';
import { createIconElement } from '@/lib';

const COMMENT_REPLY_TYPES = {
    COMMENT: 'comment',
    NO_COMMENT: 'no_comment',
} as const;

const DEFAULT_COMMENT_REPLIES = [
    'Message sent! 🚀',
    'Check your DMs! 📩',
    'Just sent you a DM! Take a look when you can!',
];

interface AutomationDetailsProps {
    automation: Automation;
    onDirtyStateChange?: (isDirty: boolean) => void;
}

/**
 * AutomationDetails - Accordion body content with form
 */
export const AutomationDetails = ({ automation, onDirtyStateChange }: AutomationDetailsProps) => {
    const automationId = automation.automation_id;

    // Comment reply dropdown options
    const COMMENT_REPLY_OPTIONS: DropdownValue[] = [
        { value: COMMENT_REPLY_TYPES.COMMENT, label: 'Comment back' },
        { value: COMMENT_REPLY_TYPES.NO_COMMENT, label: 'Do not comment' },
    ];

    // Transform automation data to form format
    const defaultValues = useMemo((): AutomationFormData => {
        if (!automation.data) {
            return getDefaultAutomationFormValues();
        }

        const isNewAutomation = automation.automation_id === undefined;

        let keywords: string[] = [];
        if (automation.data.trigger?.data?.keywords) {
            keywords = automation.data.trigger.data.keywords.split(',').map(k => k.trim());
        } else if (isNewAutomation) {
            // For new automations, use default 'link' keyword
            keywords = ['link'];
        }

        return {
            trigger_source: automation.data.trigger?.source || 'all',
            media_ids: automation.data.trigger?.media_ids || [],
            keywords,
            comment_choices: automation.data.response?.comment_choices || [],
            content: automation.data.response?.data?.content || '',
            buttons: (automation.data.response?.data?.buttons || []) as Array<{
                type: 'web_url';
                url: string;
                title: string;
            }>,
            status: automation.status === 'deleted' ? 'draft' : automation.status || 'draft',
        };
    }, [automation]);

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        setValue,
    } = useForm<AutomationFormData>({
        defaultValues,
        resolver: zodResolver(automationFormSchema),
    });

    // Watch trigger source to conditionally show comment reply section
    const triggerSource = useWatch({ control, name: 'trigger_source' });
    const shouldShowCommentReply = triggerSource === 'comment' || triggerSource === 'all';

    // Derive replyToComment from form state instead of separate state
    const commentChoices = useWatch({ control, name: 'comment_choices' });
    const replyToComment = commentChoices.length > 0 ? COMMENT_REPLY_TYPES.COMMENT : COMMENT_REPLY_TYPES.NO_COMMENT;

    // Handler for comment reply toggle
    const handleCommentReplyToggle = (value: DropdownValue) => {
        const isNewAutomation = automation.automation_id === undefined;
        if (value.value === COMMENT_REPLY_TYPES.COMMENT) {
            setValue('comment_choices', DEFAULT_COMMENT_REPLIES, { shouldDirty: !isNewAutomation });
        } else {
            setValue('comment_choices', [], { shouldDirty: true });
        }
    };

    // Event handlers that always use latest callbacks
    const resetForm = useEffectEvent((values: AutomationFormData) => {
        reset(values);
    });

    const notifyDirtyStateChange = useEffectEvent((dirty: boolean) => {
        onDirtyStateChange?.(dirty);
    });

    // Reset form when automation changes
    useEffect(() => {
        resetForm(defaultValues);
    }, [defaultValues]);

    // Notify parent of dirty state changes
    useEffect(() => {
        notifyDirtyStateChange(isDirty);
    }, [isDirty]);

    const { handleSaveDraft, handlePublish, isSaving } = useAutomationFormActions({
        automationId,
        onSuccess: () => {
            // Reset form dirty state after successful save
            reset(undefined, { keepValues: true });
        },
    });

    return (
        <form className='relative mt-2'>
            {/* Row 1: Trigger Selection + Keywords/Posts */}
            <div className='relative flex items-start pb-10'>
                {/* Vertical Line */}
                <div className='border-subtle absolute top-5 left-3.5 h-full w-0 border-l-4 md:left-4.5' />

                {/* Icon Slot */}
                <div className='relative z-10 w-12 flex-shrink-0 self-start md:w-14'>
                    <div className='bg-brand-subtle flex size-8 items-center justify-center rounded-full md:size-10'>
                        {createIconElement(IcMessage, { className: 'size-4 text-brand-main' })}
                    </div>
                </div>

                {/* Content Slot - Two Column Grid */}
                <div className='grid flex-1 gap-2 max-md:grid-cols-1 md:grid-cols-3 md:gap-8'>
                    {/* Left Column - Trigger Selector */}
                    <div className='md:col-span-1'>
                        <label htmlFor='trigger-source' className='text-label-xs-bold md:text-label-sm-bold'>
                            Trigger
                        </label>
                        <TriggerSelector control={control} error={errors.trigger_source} />
                    </div>

                    {/* Right Column - Keywords + Post Picker */}
                    <div className='space-y-4 max-md:mt-2 md:col-span-2 md:space-y-6'>
                        <KeywordInput control={control} error={errors.keywords as FieldError} />
                        <PostPicker control={control} error={errors.media_ids as FieldError} />
                    </div>
                </div>
            </div>

            {/* Row 2: Comment Reply (Conditional) - Only show when trigger is 'comment' or 'all' */}
            {shouldShowCommentReply && (
                <div className='relative flex items-start pb-10'>
                    {/* Vertical Line */}
                    <div className='border-subtle absolute top-5 left-3.5 h-full w-0 border-l-4 md:left-4.5' />

                    {/* Icon Slot */}
                    <div className='relative z-10 w-12 flex-shrink-0 self-start md:w-14'>
                        <div className='bg-brand-subtle flex size-8 items-center justify-center rounded-full md:size-10'>
                            {createIconElement(IcReverseLeft, { className: 'size-4 text-brand-main' })}
                        </div>
                    </div>

                    {/* Content Slot - Two Column Grid */}
                    <div className='grid flex-1 gap-2 max-md:grid-cols-1 md:grid-cols-3 md:gap-8'>
                        {/* Left Column - Comment Reply Dropdown */}
                        <div className='md:col-span-1'>
                            <label htmlFor='comment-reply-toggle' className='text-label-xs-bold md:text-label-sm-bold'>
                                On Comment
                            </label>
                            <StanDropdown
                                id='comment-reply-toggle'
                                name='comment-reply-toggle'
                                options={COMMENT_REPLY_OPTIONS}
                                currentValue={
                                    COMMENT_REPLY_OPTIONS.find(opt => opt.value === replyToComment) ||
                                    COMMENT_REPLY_OPTIONS[1]
                                }
                                onSelect={handleCommentReplyToggle}
                                preventDeselect
                            />
                            {replyToComment === COMMENT_REPLY_TYPES.COMMENT && (
                                <div className='mt-1 -space-y-1'>
                                    <p className='text-body-xs'>Replying to comments has risks—stay safe!</p>
                                    <a
                                        href='https://help.stan.store/article/383-ig-auto-reply-auto-comment-replies'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-body-xs text-brand-main hover:text-brand-hover! font-medium focus-visible:underline!'
                                    >
                                        Learn more.
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Comment Reply Field (Conditional) */}
                        {replyToComment === COMMENT_REPLY_TYPES.COMMENT && (
                            <div className='md:col-span-2'>
                                <CommentReplyField control={control} error={errors.comment_choices as FieldError} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Row 3: Auto-DM Message */}
            <div className='relative flex items-start pb-4'>
                {/* Icon Slot */}
                <div className='relative z-10 w-12 flex-shrink-0 self-start md:w-14'>
                    <div className='bg-brand-subtle flex size-8 items-center justify-center rounded-full md:size-10'>
                        {createIconElement(IcSend01, { className: 'size-4 text-brand-main' })}
                    </div>
                </div>

                {/* Content Slot - Two Column Grid */}
                <div className='grid flex-1 grid-cols-3 gap-8 max-md:mt-2 max-md:grid-cols-1'>
                    {/* Left Column - Action Text */}
                    <div className='max-md:hidden'>
                        <div className='flex h-10 items-center'>
                            <p className='text-dark text-label-sm-bold'>Send a DM</p>
                        </div>
                    </div>

                    {/* Right Column - Auto-DM Field */}
                    <div className='md:col-span-2'>
                        <AutoDMField
                            control={control}
                            error={errors.content as FieldError}
                            buttonsError={errors.buttons as FieldErrors}
                        />
                    </div>
                </div>
            </div>

            {/* Form Actions */}
            <div className='flex w-full items-center justify-end gap-1 pt-4 max-md:flex-col md:gap-2'>
                <StanButton
                    name='automation-form-actions-save-draft'
                    variant='outline'
                    onClick={handleSubmit(handleSaveDraft)}
                    disabled={isSaving || (automation.status === 'draft' && !isDirty)}
                    loading={isSaving}
                    className='max-md:w-full'
                >
                    Save Draft
                </StanButton>
                <StanButton
                    name='automation-form-actions-publish'
                    onClick={handleSubmit(handlePublish)}
                    disabled={isSaving || (automation.status === 'published' && !isDirty)}
                    loading={isSaving}
                    className='max-md:w-full'
                >
                    {automation.status === 'published' ? 'Update & Publish' : 'Publish'}
                </StanButton>
            </div>
        </form>
    );
};
